import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Search, Download, ArrowDownToLine, CheckCircle2, XCircle,
  Clock, Loader2, TrendingUp, DollarSign, Filter, Eye, X, RefreshCw,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";

const statusConfigDefault = {
  success:    { icon: CheckCircle2, color: "#34d399", bg: "rgba(52,211,153,0.1)",  border: "rgba(52,211,153,0.25)",  label: "Success" },
  pending:    { icon: Clock,        color: "#fbbf24", bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.25)",  label: "Pending" },
  processing: { icon: Loader2,      color: "#22d3ee", bg: "rgba(34,211,238,0.1)",  border: "rgba(34,211,238,0.25)",  label: "Processing" },
  failed:     { icon: XCircle,      color: "#f87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)", label: "Failed" },
};

const getStatusConfig = (t) => {
  if (!t) return statusConfigDefault;
  return {
    success:    { ...statusConfigDefault.success,    label: t("success") },
    pending:    { ...statusConfigDefault.pending,    label: t("pending") },
    processing: { ...statusConfigDefault.processing, label: t("processing") },
    failed:     { ...statusConfigDefault.failed,     label: t("failed") },
  };
};

const statusKeys = ["all", "success", "pending", "processing", "failed"];

const money = (value) => `฿${Number(value || 0).toLocaleString()}`;

const normalizeStatus = (rawStatus) => {
  const key = String(rawStatus || "").trim().toLowerCase();
  if (["success", "completed", "paid"].includes(key)) return "success";
  if (["processing", "in_progress"].includes(key)) return "processing";
  if (["failed", "cancelled", "canceled", "expired", "timeout"].includes(key)) return "failed";
  return "pending";
};

/* ─── Premium Stat Card ─────────────────────────────────────── */
function StatCard({ title, value, icon: Icon, gradient, accent, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 22, scale: 0.93 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, type: "spring", stiffness: 180, damping: 18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative"
      style={{ perspective: 900 }}
    >
      <motion.div
        animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1.08 : 0.85 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${accent}1e, transparent 68%)`, filter: "blur(10px)" }}
      />
      <motion.div
        animate={{ y: hovered ? -8 : 0, rotateX: hovered ? -2 : 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 24 }}
        className="relative rounded-2xl overflow-hidden p-5"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.92))",
          border: `1px solid ${hovered ? `${accent}38` : "rgba(148,163,184,0.1)"}`,
          boxShadow: hovered ? `0 20px 55px rgba(0,0,0,0.52), 0 0 30px ${accent}18` : "0 4px 18px rgba(0,0,0,0.3)",
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}55, transparent)` }} />
        <motion.div
          animate={{ x: hovered ? "108%" : "-108%" }}
          transition={{ duration: 1.15, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none" style={{ background: `radial-gradient(circle at 100% 0%, ${accent}0a, transparent 70%)` }} />

        <div className="relative flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
            <motion.p animate={{ scale: hovered ? 1.04 : 1 }} className="text-2xl font-bold text-slate-100 leading-none">{value}</motion.p>
          </div>
          <motion.div
            animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 6 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${gradient})`,
              boxShadow: hovered ? `0 0 22px ${accent}44` : `0 4px 14px ${accent}28`,
              transition: "box-shadow 0.3s",
            }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function LeftPreviewPanel({ transaction, onClose }) {
  if (!transaction) return null;

  const statusKey = String(transaction.status || "pending").toLowerCase();
  const status = statusConfigDefault[statusKey] || statusConfigDefault.pending;
  const StatusIcon = status.icon;

  const primaryRows = [
    ["Transaction", transaction.transaction_id || "-"],
    ["Merchant", transaction.merchant_code || "-"],
    ["Provider", transaction.provider_code || "-"],
    ["Payment Method", transaction.payment_method || "-"],
  ];

  const timelineRows = [
    ["Created", transaction.created_date ? format(new Date(transaction.created_date), "MMM d, HH:mm:ss") : "-"],
    ["Processed", transaction.processed_at ? format(new Date(transaction.processed_at), "MMM d, HH:mm:ss") : "-"],
    ["Completed", transaction.completed_at ? format(new Date(transaction.completed_at), "MMM d, HH:mm:ss") : "-"],
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(2, 6, 23, 0.65)", zIndex: 70 }}
      />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(420px, 92vw)",
          background: "linear-gradient(180deg, #070f1f 0%, #040a15 100%)",
          borderLeft: "1px solid rgba(34,211,238,0.2)",
          zIndex: 71,
          overflowY: "auto",
          boxShadow: "-24px 0 54px rgba(0,0,0,0.52)",
        }}
      >
        <div
          style={{
            padding: 18,
            borderBottom: "1px solid rgba(148,163,184,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(180deg, rgba(34,211,238,0.07), rgba(2,6,23,0))",
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: "#e2e8f0", fontSize: 18, fontWeight: 800 }}>Deposit Preview</h3>
            <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 12 }}>Transaction details snapshot</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            style={{ width: 34, height: 34, borderRadius: 10, border: "1px solid rgba(148,163,184,0.25)", background: "rgba(15,23,42,0.8)", color: "#cbd5e1", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{
              padding: 14,
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.16)",
              background: "rgba(15,23,42,0.66)",
            }}
          >
            <p style={{ margin: 0, color: "#64748b", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700 }}>Amount</p>
            <p style={{ margin: "6px 0 10px", color: "#34d399", fontSize: 28, fontWeight: 900 }}>{`+${money(transaction.amount)}`}</p>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 999,
                border: `1px solid ${status.border}`,
                background: status.bg,
                color: status.color,
                fontSize: 12,
                fontWeight: 700,
                textTransform: "capitalize",
              }}
            >
              <StatusIcon className={`h-3.5 w-3.5 ${statusKey === "processing" ? "animate-spin" : ""}`} />
              {status.label}
            </div>
          </div>

          <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Details</div>
          {primaryRows.map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(148,163,184,0.13)", background: "rgba(15,23,42,0.6)" }}>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>{label}</span>
              <span style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
            </div>
          ))}

          <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 4 }}>Timeline</div>
          {timelineRows.map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(148,163,184,0.13)", background: "rgba(15,23,42,0.6)" }}>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>{label}</span>
              <span style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
            </div>
          ))}

          {transaction.error_message && (
            <div style={{ padding: 12, borderRadius: 10, border: "1px solid rgba(248,113,113,0.26)", background: "rgba(248,113,113,0.09)" }}>
              <p style={{ margin: 0, color: "#fca5a5", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>Error</p>
              <p style={{ margin: "6px 0 0", color: "#fecaca", fontSize: 12 }}>{transaction.error_message}</p>
            </div>
          )}
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineDeposits() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const resellerName = "Gaming Partners Ltd";
  const statusConfig = useMemo(() => getStatusConfig(t), [t]);

  const { data: deposits = [], isLoading, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["engine-deposits"],
    queryFn: async () => {
      const engineQRCodes = await qrCodeService.getEngineQRCodes();
      return engineQRCodes || [];
    },
    refetchInterval: 5000,
    staleTime: 0,
  });

  const normalizedTransactions = useMemo(
    () =>
      deposits.map((qr) => ({
        ...qr,
        // Map QR rows into the transaction-like UI contract used by this screen.
        transaction_id: qr?.payment_id || qr?.qr_code_id,
        merchant_code: qr?.merchant?.code || "-",
        provider_code: "QR",
        payment_method: "QR",
        created_date: qr?.createdAt,
        amount: Number(qr?.amount ?? 0) || 0,
        status: normalizeStatus(qr?.status),
        type: "deposit",
      })),
    [deposits],
  );

  const filteredTransactions = normalizedTransactions.filter(tx => {
    if (
      search &&
      ![
        tx.transaction_id,
        tx.merchant_code,
        tx.provider_code,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(search.toLowerCase()))
    ) return false;
    if (statusFilter !== "all" && String(tx.status).toLowerCase() !== statusFilter) return false;
    return true;
  });

  const statusCounts = useMemo(() => {
    const counts = { all: normalizedTransactions.length, success: 0, pending: 0, processing: 0, failed: 0 };
    normalizedTransactions.forEach((tx) => {
      const key = String(tx.status || "").toLowerCase();
      if (key in counts) counts[key] += 1;
    });
    return counts;
  }, [normalizedTransactions]);

  const totalAmount  = filteredTransactions.filter(t => t.status === "success").reduce((s, t) => s + (t.amount || 0), 0);
  const successCount = filteredTransactions.filter(t => t.status === "success").length;
  const pendingCount = filteredTransactions.filter(t => t.status === "pending" || t.status === "processing").length;

  return (
    <EngineLayout currentPage="EngineDeposits" resellerName={resellerName}>
      <div className="space-y-6 w-full max-w-full">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.92))",
            border: "1px solid rgba(52,211,153,0.14)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(52,211,153,0.5), rgba(34,211,238,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-48 h-48 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(52,211,153,0.07), transparent 70%)" }} />

          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-400 mb-1">Payment Gateway</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-1">{t("deposits")}</h1>
              <p className="text-sm text-slate-400">
                {t("total")}: <span className="text-emerald-400 font-bold">{money(totalAmount)}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="inline-flex items-center gap-2 px-3 h-9 rounded-xl text-xs font-semibold"
                style={{ border: "1px solid rgba(34,211,238,0.22)", background: "rgba(34,211,238,0.08)", color: "#67e8f9" }}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
                {dataUpdatedAt ? `Live ${format(new Date(dataUpdatedAt), "HH:mm:ss")}` : "Live"}
              </div>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.96 }}
                className="flex items-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold cursor-pointer outline-none"
                style={{
                  background: "rgba(52,211,153,0.08)",
                  border: "1px solid rgba(52,211,153,0.2)",
                  color: "#34d399",
                  transition: "background 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(52,211,153,0.14)"; e.currentTarget.style.boxShadow = "0 0 16px rgba(52,211,153,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(52,211,153,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <Download className="h-4 w-4" />
                {t("export")}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard title={t("totalVolume")}         value={`฿${totalAmount.toLocaleString()}`} icon={DollarSign}   gradient="#22d3ee, #0284c7" accent="#22d3ee" delay={0} />
          <StatCard title={t("successfulDeposits")}  value={successCount}                       icon={CheckCircle2} gradient="#34d399, #059669" accent="#34d399" delay={0.05} />
          <StatCard title={t("pendingDeposits")}     value={pendingCount}                        icon={Clock}        gradient="#fbbf24, #d97706" accent="#fbbf24" delay={0.1} />
        </div>

        {/* ── Filters ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.13 }}
          className="relative rounded-2xl overflow-hidden p-4"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.92))",
            border: "1px solid rgba(148,163,184,0.09)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(52,211,153,0.25), transparent)" }} />

          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Filters</span>
          </div>

          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative flex-1" style={{ minWidth: 200 }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: searchFocused ? "#34d399" : "#475569" }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`${t("search")} by tx / merchant / provider...`}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="w-full h-10 pl-9 pr-4 rounded-xl text-sm text-slate-200 placeholder:text-slate-600 outline-none"
                style={{
                  background: "rgba(2,6,23,0.7)",
                  border: `1px solid ${searchFocused ? "rgba(52,211,153,0.3)" : "rgba(148,163,184,0.1)"}`,
                  boxShadow: searchFocused ? "0 0 0 3px rgba(52,211,153,0.07)" : "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
              />
            </div>

            {/* Status */}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-10 px-3 rounded-xl text-sm outline-none cursor-pointer"
              style={{
                background: "rgba(2,6,23,0.7)",
                border: "1px solid rgba(148,163,184,0.1)",
                color: "#94a3b8",
                transition: "border-color 0.2s",
              }}
              onFocus={e => e.target.style.borderColor = "rgba(52,211,153,0.3)"}
              onBlur={e => e.target.style.borderColor = "rgba(148,163,184,0.1)"}
            >
              <option value="all">{t("allStatus")}</option>
              <option value="success">{t("success")}</option>
              <option value="pending">{t("pending")}</option>
              <option value="failed">{t("failed")}</option>
            </select>

            {(search || statusFilter !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                }}
                className="h-10 px-3 rounded-xl text-xs font-semibold"
                style={{ background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.25)", color: "#fca5a5" }}
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {statusKeys.map((key) => {
              const active = statusFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  className="h-8 px-3 rounded-lg text-xs font-semibold"
                  style={{
                    background: active ? "rgba(34,211,238,0.16)" : "rgba(15,23,42,0.7)",
                    border: `1px solid ${active ? "rgba(34,211,238,0.4)" : "rgba(148,163,184,0.18)"}`,
                    color: active ? "#67e8f9" : "#94a3b8",
                  }}
                >
                  {key === "all" ? t("allStatus") : t(key)} ({statusCounts[key] || 0})
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* ── Table ── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.92))",
            border: "1px solid rgba(148,163,184,0.09)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(52,211,153,0.28), transparent)" }} />

          <div className="overflow-x-auto" style={{ maxHeight: "58vh" }}>
            <table className="w-full" style={{ minWidth: 580 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.08)", background: "rgba(2,6,23,0.92)", position: "sticky", top: 0, zIndex: 2 }}>
                  {[
                    { label: t("transaction"), align: "left" },
                    { label: t("merchant"), align: "left", hide: "sm" },
                    { label: t("amount"), align: "right" },
                    { label: t("status"), align: "left" },
                    { label: t("date"), align: "left", hide: "md" },
                    { label: "", align: "right" },
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`px-4 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 text-${h.align} ${h.hide === "sm" ? "hidden sm:table-cell" : ""} ${h.hide === "md" ? "hidden md:table-cell" : ""}`}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="wait">
                  {isLoading ? (
                    <tr><td colSpan={6} className="text-center py-14">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-5 w-5 text-emerald-400 animate-spin" />
                        <span className="text-sm text-slate-500">{t("loading")}</span>
                      </div>
                    </td></tr>
                  ) : filteredTransactions.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-14 text-slate-600 text-sm">{t("noDeposits")}</td></tr>
                  ) : filteredTransactions.slice(0, 50).map((tx, i) => {
                    const statusKey = (tx.status || "").toLowerCase();
                    const st = statusConfig[statusKey] || statusConfig.pending;
                    const StatusIcon = st.icon;
                    return (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.025 }}
                        onClick={() => setSelectedTx(tx)}
                        className="cursor-pointer"
                        style={{ borderBottom: "1px solid rgba(148,163,184,0.05)", transition: "background 0.18s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(52,211,153,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            <motion.div
                              whileHover={{ scale: 1.1, rotate: 6 }}
                              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.2), rgba(2,132,199,0.12))", border: "1px solid rgba(34,211,238,0.18)" }}
                            >
                              <ArrowDownToLine className="h-4 w-4 text-cyan-400" />
                            </motion.div>
                            <div>
                              <p className="font-mono text-sm text-slate-200">{tx.transaction_id?.slice(0, 10)}...</p>
                              <p className="text-xs text-slate-500 sm:hidden">{tx.merchant_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 hidden sm:table-cell text-sm text-slate-400">{tx.merchant_code || t("nA")}</td>
                        <td className="px-4 py-3.5 text-right font-bold" style={{ color: "#34d399" }}>+{money(tx.amount)}</td>
                        <td className="px-4 py-3.5">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: st.bg, border: `1px solid ${st.border}` }}>
                            <StatusIcon className={`h-3 w-3 ${tx.status === "processing" ? "animate-spin" : ""}`} style={{ color: st.color }} />
                            <span style={{ color: st.color }}>{st.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-xs text-slate-500 hidden md:table-cell">
                          {tx.created_date ? format(new Date(tx.created_date), "MMM d, HH:mm") : t("nA")}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <button
                            type="button"
                            aria-label="Preview deposit details"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTx(tx);
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
                            style={{
                              border: "1px solid rgba(34,211,238,0.2)",
                              background: "rgba(34,211,238,0.08)",
                              color: "#22d3ee",
                              transition: "all .2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "rgba(34,211,238,0.16)";
                              e.currentTarget.style.borderColor = "rgba(34,211,238,0.4)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "rgba(34,211,238,0.08)";
                              e.currentTarget.style.borderColor = "rgba(34,211,238,0.2)";
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>

        <LeftPreviewPanel transaction={selectedTx} onClose={() => setSelectedTx(null)} />
      </div>
    </EngineLayout>
  );
}